/**
 * Journal posts — Volume II.
 *
 * Ten new entries built to break the rhythm of the original twenty.
 * Each one has a distinct opening cadence, a single concrete analogy
 * carried through (waiting room, background apps, old shoes, endurance
 * vs sprint, etc.), a different emotional angle, and a CTA chosen
 * because it fits the post — not because it's the default.
 *
 * Brand-vocabulary discipline: "signal", "frequency", "alignment",
 * "blueprint", "momentum", "shift" are used sparingly. Most posts
 * lean on plain English so the writing reads like Cyrus thinking out
 * loud, not a brand voice asserting itself.
 */

const PUBLISHED_V2 = "2026-05-25";
const UPDATED_V2 = "2026-05-25";

const JOURNAL_POSTS_V2 = [
  // ────────────────────────────────────────────────────────────────
  // 1. THE WAITING ROOM IS NOT WASTED TIME
  // ────────────────────────────────────────────────────────────────
  {
    id: 21,
    slug: "waiting-room-is-not-wasted-time",
    title: "The Waiting Room Is Not Wasted Time.",
    excerpt:
      "Some seasons look like nothing is happening. They are actually where your capacity is being built before the next thing arrives.",
    metaTitle: "Why the Waiting Season Is Not Wasted Time",
    metaDescription:
      "The season where nothing seems to be happening is often the one quietly building the capacity your next chapter will require.",
    ogTitle: "Why the Waiting Season Is Not Wasted Time",
    ogDescription:
      "Delay is not always denial. Sometimes it is preparation you cannot see yet.",
    primarySeoPhrase: "waiting season meaning",
    aeoQuestion: "Why does it feel like nothing is happening in my life?",
    themeTags: ["Transition"],
    primaryPath: "snapshot",
    readingTime: "4 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "waiting-room",
      prompt:
        "A quiet hospital waiting room at twilight. One single chair lit by warm gold light, the rest in soft shadow. A folded magazine resting on the seat. Cinematic editorial still life, premium magazine feel, no people.",
      alt: "A single gold-lit chair in a quiet waiting room, symbolizing a season of patient preparation.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Some chapters look empty from the outside while doing the most important work on the inside. If nothing seems to be moving, your capacity may be the thing being built. Delay is not always denial. Sometimes it is preparation.",
    opening: [
      "Nobody puts a waiting room on a vision board.",
      "The picture you keep showing people is the after. The promotion. The launch. The healed relationship. The body you trust again. The version of life you can finally point to.",
      "Nobody photographs the in between.",
      "But every meaningful chapter starts there. Before the surgery, before the verdict, before the move, before the answer, there is a room with bad lighting and old magazines and a feeling that time has stopped paying attention to you.",
      "You are not stalled. You are being prepared.",
    ],
    bodySections: [
      {
        heading: "Capacity is invisible",
        paragraphs: [
          "You can measure income. You can measure followers. You can measure how many emails you sent and how many calls you booked.",
          "You cannot measure capacity in the moment it is being built. You only notice it later, when something heavy lands and you do not break the way you used to break.",
          "The work happening inside you right now will look like nothing on your calendar.",
          "That does not make it nothing.",
        ],
      },
      {
        heading: "Stop confusing stillness with absence",
        paragraphs: [
          "Most people interpret quiet as evidence that they are off track.",
          "So they fill the room. They add another podcast. Another course. Another conversation about the same problem. They mistake friction for proof of effort.",
          "But the most expensive thing you can do in a real preparation season is interrupt it.",
          "If the room is quiet, the room is doing something. Sit in it long enough to learn what it is teaching.",
        ],
      },
      {
        heading: "What the waiting room actually does",
        paragraphs: [
          "It removes urgency long enough for you to hear what you actually want.",
          "It separates the people who can hold a pause from the people who panic and call it action.",
          "It makes you choose between staying loyal to your old timeline or trusting a slower one that has not been wrong yet.",
        ],
      },
      {
        heading: "When the door opens",
        paragraphs: [
          "It will not feel like applause.",
          "It will feel like a name being called from across the room. Quiet. Specific. Yours.",
          "The work of the waiting was hearing that name without panicking it into existence.",
        ],
      },
    ],
    closingInvitation:
      "If you have been sitting in the quiet wondering if you missed something, the Clarity Snapshot was built to surface what the silence is actually preparing you for.",
    faqs: [
      {
        q: "Why does it feel like nothing is happening in my life?",
        a: "Some chapters do their work invisibly. Capacity, recovery, and identity work rarely look productive in the moment. What looks like nothing is often preparation.",
      },
      {
        q: "How do I tell the difference between waiting and stalling?",
        a: "Waiting has quiet underneath it. Stalling has tension. If the pause includes relief, listen to it. If the pause includes avoidance, name what you are avoiding.",
      },
    ],
    reflectionQuestion:
      "What is one thing the quiet season may be building in you that you could not have learned in motion?",
    ctaLabel: "Take the Clarity Snapshot",
    ctaHref: "/frequency-snapshot",
    relatedSlugs: ["want-clarity-rewarding-chaos", "next-level-feels-like-grief", "moment-before-clarity-feels-like-confusion"],
  },

  // ────────────────────────────────────────────────────────────────
  // 2. YOU ARE NOT LAZY. YOU ARE OVERLOADED.
  // ────────────────────────────────────────────────────────────────
  {
    id: 22,
    slug: "you-are-not-lazy-you-are-overloaded",
    title: "You Are Not Lazy. You Are Overloaded.",
    excerpt:
      "Most people who think they have a discipline problem actually have a too-many-open-loops problem. Clarity comes from subtraction.",
    metaTitle: "You Are Not Lazy. You Are Overloaded.",
    metaDescription:
      "Most exhaustion is not laziness. It is the cost of running too many emotional tabs in the background. Clarity comes from closing some.",
    ogTitle: "You Are Not Lazy. You Are Overloaded.",
    ogDescription:
      "If your battery dies before noon, it is probably not motivation. It is the apps you forgot to close.",
    primarySeoPhrase: "feel lazy but exhausted",
    aeoQuestion: "Why do I feel lazy even when I am exhausted?",
    themeTags: ["Clarity"],
    primaryPath: "orientation_reset",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "background-apps",
      prompt:
        "A phone screen on a dark surface, dimmed to almost off, with dozens of small open app icons visible at the edges suggesting too many things running. A single golden notification glow in the center. Cinematic editorial still life.",
      alt: "A dimming phone screen with too many background apps running, symbolizing emotional overload.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "What looks like laziness is often the cost of running too many emotional tabs in the background. You are not low on motivation. You are low on bandwidth.",
    opening: [
      "Open your phone.",
      "Count the apps still running in the background that you never closed. The browser tab from a Tuesday afternoon. The conversation you minimized but never replied to. The download that never finished.",
      "That is your life right now.",
      "You think you are lacking discipline because you cannot make yourself do the one thing you said you would do.",
      "You are not lacking discipline.",
      "You are running thirty-eight things in the background and asking yourself why the screen keeps dimming.",
    ],
    bodySections: [
      {
        heading: "Open loops cost you energy",
        paragraphs: [
          "An open loop is anything that did not get a clean ending.",
          "The email you owe but have not sent. The relationship you ended but never told yourself you ended. The decision you delayed for so long that it became its own ongoing weight.",
          "Each one is small. Each one is light. Each one is also using a sliver of the energy you keep trying to save for something important.",
          "Add up enough slivers and you wake up tired before the day starts.",
        ],
      },
      {
        heading: "The real first step is closing, not adding",
        paragraphs: [
          "Most advice tells you to add. Add a habit. Add a system. Add an app to track the apps.",
          "But you cannot add your way out of overload.",
          "The first move is closing something. One conversation. One commitment. One promise to yourself you keep breaking that you should either keep or formally retire.",
          "Closing a loop returns the energy that loop has been draining.",
        ],
      },
      {
        heading: "Decision is the cheapest cleanup tool you own",
        paragraphs: [
          "You can keep something open and unresolved for years.",
          "Or you can decide. Yes. No. Not now. Done.",
          "A decision does not have to be the right one forever. It just has to close the loop long enough for your bandwidth to return.",
          "You will be surprised how much of your fatigue was being caused by things you never officially called.",
        ],
      },
      {
        heading: "Quiet is not laziness either",
        paragraphs: [
          "Once the background quiets, you may discover you are not the high-output machine you thought you were.",
          "You may be a person who creates well from rested attention. Who thinks well when the room is not full of half-finished agreements.",
          "That is not lazy. That is functional.",
        ],
      },
    ],
    closingInvitation:
      "If you cannot feel where the load is heaviest, the Clarity Reset Session was built for that. One conversation. One pass through what is open. A short list of what is worth closing first.",
    faqs: [
      {
        q: "Why do I feel lazy even when I am exhausted?",
        a: "Exhaustion and laziness are usually unrelated. What looks like laziness is often the result of running too many unresolved decisions, conversations, and obligations in the background.",
      },
      {
        q: "How do I close an emotional open loop?",
        a: "Name what is open. Choose between yes, no, or not now. Communicate the choice where it matters. Even a partial close returns energy.",
      },
    ],
    reflectionQuestion:
      "What is one open loop you have been carrying for six months or longer that you could close this week?",
    ctaLabel: "Book the Clarity Reset Session",
    ctaHref: "#book-orientation-reset",
    relatedSlugs: ["burned-out-betraying-your-energy", "life-you-want-too-loud-to-notice", "you-do-not-need-noise-cleaner-question"],
  },

  // ────────────────────────────────────────────────────────────────
  // 3. STOP CONFUSING FAMILIAR WITH SAFE
  // ────────────────────────────────────────────────────────────────
  {
    id: 23,
    slug: "stop-confusing-familiar-with-safe",
    title: "Stop Confusing Familiar With Safe.",
    excerpt:
      "Some of the patterns you call comfortable are actually damaging you. They just feel like home because you have worn them long enough.",
    metaTitle: "Stop Confusing Familiar With Safe",
    metaDescription:
      "Loyalty to a pattern is not the same as safety. Most of what hurts us repeats because we recognize it, not because it is good for us.",
    ogTitle: "Stop Confusing Familiar With Safe",
    ogDescription:
      "Old shoes feel comfortable until you notice your back hurts. Familiarity is not the same as care.",
    primarySeoPhrase: "familiar is not safe",
    aeoQuestion: "Why do I keep going back to things that hurt me?",
    themeTags: ["Inner Work"],
    primaryPath: "start-here",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "old-shoes",
      prompt:
        "A pair of beautifully worn-in old leather shoes resting on a dark wooden floor, soft warm gold light falling diagonally. The shoes look loved and broken in, but the heels are visibly damaged. Cinematic editorial still life.",
      alt: "Well-worn leather shoes with damaged heels, symbolizing a familiar pattern that quietly hurts you.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Comfort is not always safety. Sometimes it is repetition. The fact that something feels familiar is not evidence that it is good for you. It is evidence that you have known it long enough to stop noticing the damage.",
    opening: [
      "There is a pair of shoes you have worn for years.",
      "They are comfortable. They are easy. You can put them on without looking. They feel like yours.",
      "They are also slowly ruining your back.",
      "You do not notice because the damage is not loud. It happens in small degrees. A tightness in the hip. A click in the knee. A discomfort you have explained away so many times it does not register as a signal anymore.",
      "Now read that paragraph again, and replace the shoes with a person, a job, a habit, a way of speaking to yourself, or a story you keep telling about who you are.",
    ],
    bodySections: [
      {
        heading: "Familiarity has its own gravity",
        paragraphs: [
          "Your brain is wired to prefer the known. Anything you can predict costs less energy than anything you have to evaluate.",
          "This is useful for driving home from work. It is dangerous for choosing what to keep in your life.",
          "Because the things that hurt you most predictably are still the things that hurt you. The cost just gets quieter the longer you stay.",
        ],
      },
      {
        heading: "Some patterns sell themselves as loyalty",
        paragraphs: [
          "Staying in something that no longer fits often gets framed as devotion.",
          "You tell yourself you are not the kind of person who walks away. You stay because someone needs you. You stay because you said you would. You stay because leaving would mean admitting that something you invested in was not what you wanted.",
          "There is a difference between loyalty and inertia. Loyalty is a choice you keep making with open eyes. Inertia is the absence of choice dressed up as one.",
        ],
      },
      {
        heading: "Safety has a different texture",
        paragraphs: [
          "Real safety is not the same as familiarity.",
          "Familiarity says you can predict what is about to happen. Safety says what is about to happen will not require you to abandon yourself to survive it.",
          "If you have to shrink, soften, edit, perform, or apologize for yourself to keep something stable, that thing is not actually safe. You have just gotten very good at the choreography.",
        ],
      },
      {
        heading: "Try a new pair on",
        paragraphs: [
          "You do not have to throw the old ones out today.",
          "You just have to be honest about whether what you have been calling comfortable has been comfortable for you, or comfortable for the version of you that learned not to ask for better.",
          "Notice the difference between the body settling and the body bracing. The body knows. It has always known.",
        ],
      },
    ],
    closingInvitation:
      "If you are not sure which patterns you have normalized, Start Here. The first read is for you. Bring nothing but honesty.",
    faqs: [
      {
        q: "Why do I keep going back to things that hurt me?",
        a: "Because your nervous system prefers the predictable over the unfamiliar, even when the predictable is painful. Recognizing the pattern is the first interruption.",
      },
      {
        q: "How do I know if a relationship or habit is unsafe versus uncomfortable?",
        a: "Discomfort asks you to grow. Lack of safety asks you to disappear. The body usually knows the difference, even when the mind argues.",
      },
    ],
    reflectionQuestion:
      "What is one pattern in your life you call comfortable that you would not recommend to someone you love?",
    ctaLabel: "Start Here",
    ctaHref: "/start-here",
    relatedSlugs: ["not-stuck-loyal-to-old-version", "defending-the-wound", "discipline-or-self-abandonment"],
  },

  // ────────────────────────────────────────────────────────────────
  // 4. THE LIFE YOU WANT REQUIRES A DIFFERENT PACE
  // ────────────────────────────────────────────────────────────────
  {
    id: 24,
    slug: "life-you-want-requires-different-pace",
    title: "The Life You Want Requires a Different Pace.",
    excerpt:
      "You cannot build something sustainable using the same rhythm that burned you out. Pace is not a soft topic. Pace is a survival skill.",
    metaTitle: "The Life You Want Requires a Different Pace",
    metaDescription:
      "Speed is not the same as progress. Sustainable change requires a pace your body and attention can keep without breaking.",
    ogTitle: "The Life You Want Requires a Different Pace",
    ogDescription:
      "You cannot sprint your way into a life that requires endurance. Choose your pace before the pace chooses you.",
    primarySeoPhrase: "how to slow down and still progress",
    aeoQuestion: "Why does the life I want keep wearing me out?",
    themeTags: ["Discipline"],
    primaryPath: "blueprint",
    readingTime: "6 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "endurance",
      prompt:
        "An empty running track at dawn, gold light catching one lane. No runner in frame. A single discarded sprint stopwatch resting at the edge. Cinematic editorial still life, premium magazine.",
      alt: "An empty track at dawn with a stopwatch beside it, symbolizing a shift from sprint to endurance.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "The pace that got you here will not carry you to what is next. Speed feels like progress for a while. Then it taxes the system that was supposed to enjoy the destination. A sustainable pace is not the slow option. It is the only one that survives the distance.",
    opening: [
      "Most people who burn out were not chasing the wrong thing.",
      "They were running the right race at the wrong pace.",
      "There is a kind of person who treats every season like a sprint. The launch. The pivot. The repair. The healing. All of it on a 100-meter timeline because that is the only speed they ever learned.",
      "They get to the next mile and wonder why their legs are shaking.",
    ],
    bodySections: [
      {
        heading: "Sprinting is a skill. Endurance is a different one.",
        paragraphs: [
          "Sprinting rewards explosion. Endurance rewards stewardship.",
          "Sprinting asks how much can I give in the next sixty seconds. Endurance asks how do I still have something left at mile nine.",
          "Most of the things you actually want in your life are mile-nine projects. The relationship. The body. The work that compounds. The income that does not depend on you running yourself into the ground every quarter.",
          "You cannot sprint mile nine. You can only pace it.",
        ],
      },
      {
        heading: "Pace is not soft",
        paragraphs: [
          "Slowing down is often framed as the gentle option. It is not gentle. It is structural.",
          "A sustainable pace is the difference between someone who is still doing the work a decade from now and someone who quietly disappeared after two good years.",
          "If your pace requires you to keep cancelling your life to maintain it, that pace is not yours. It belongs to whatever you are running from.",
        ],
      },
      {
        heading: "Recovery is part of the work, not a reward for finishing it",
        paragraphs: [
          "Athletes do not earn rest. They schedule it as part of the program.",
          "Most people treat rest as the prize for working hard enough. Which means they only rest when they collapse.",
          "Try this instead. Build recovery into the next chapter before the next chapter requires it. A weekly window. A monthly day. A quarterly nothing-week. The body learns very quickly that you mean it.",
        ],
      },
      {
        heading: "Pace tells the truth about your real timeline",
        paragraphs: [
          "If you cannot do something at a humane pace, it is worth asking whether the timeline was ever realistic, or whether you accepted someone else's deadline as if it were your own.",
          "A real timeline is one you can keep without resenting the goal by the time you reach it.",
        ],
      },
    ],
    closingInvitation:
      "The Personal Action Plan is the long view. It maps what you are building, what each phase actually requires, and the pace you can hold without erasing yourself in the process.",
    faqs: [
      {
        q: "Why does the life I want keep wearing me out?",
        a: "Because you may be running the right direction at a pace built for short distance. Outcomes that compound require pacing that compounds too.",
      },
      {
        q: "How do I know if my pace is sustainable?",
        a: "Ask whether you could keep this exact week up for a year without needing to cancel your relationships, body, or attention to maintain it. If the answer is no, the pace is borrowed.",
      },
    ],
    reflectionQuestion:
      "If your current pace had to be held for the next five years exactly as it is, what would still be standing?",
    ctaLabel: "Explore the Personal Action Plan",
    ctaHref: "/?checkout=blueprint#blueprint",
    relatedSlugs: ["burned-out-betraying-your-energy", "addicted-to-potential", "afraid-to-start-small"],
  },

  // ────────────────────────────────────────────────────────────────
  // 5. YOUR NEXT LEVEL MAY FIRST FEEL LIKE GRIEF
  // ────────────────────────────────────────────────────────────────
  {
    id: 25,
    slug: "next-level-feels-like-grief",
    title: "Your Next Level May First Feel Like Grief.",
    excerpt:
      "Growth often arrives wearing the costume of loss. You are not regressing. You are mourning the version of yourself that survived the last chapter.",
    metaTitle: "Why Growth Sometimes Feels Like Grief",
    metaDescription:
      "Becoming someone new often requires letting go of the version of you who survived the old chapter. That goodbye is real grief, and it is allowed.",
    ogTitle: "Why Growth Sometimes Feels Like Grief",
    ogDescription:
      "If your next level feels like loss, you are not broken. You are mid-goodbye.",
    primarySeoPhrase: "grief of personal growth",
    aeoQuestion: "Why does growth feel like loss?",
    themeTags: ["Healing"],
    primaryPath: "start-here",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "house-moving",
      prompt:
        "An empty house at dusk, last cardboard box by the open front door, warm gold light from a single hanging bulb. Outside the door, a dark unknown driveway. Cinematic editorial still life, soft and emotional.",
      alt: "A last box in an empty house at the threshold, symbolizing the grief of growing into something new.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Becoming new can feel like loss before it feels like freedom. You are not regressing. You are mourning the version of you that knew how to live in a house that no longer fits.",
    opening: [
      "The day you move out of an old house, the new place is exciting.",
      "Then you walk through the empty rooms one more time and your chest collapses a little.",
      "Not because you want to stay. Because you remember.",
      "The chair where you cried. The wall where you mounted a picture you no longer own. The corner where you became a slightly braver version of yourself.",
      "Growth is rarely just arrival. It is also a goodbye most people will never ask you about.",
    ],
    bodySections: [
      {
        heading: "Mourning is not regression",
        paragraphs: [
          "When the new version of you arrives, the old one does not always exit quietly.",
          "She made it through some things. She protected you. She figured out how to keep going when the room was unkind. She earned a goodbye.",
          "What people call relapse is sometimes just an unfinished farewell.",
        ],
      },
      {
        heading: "Joy and loss can live in the same week",
        paragraphs: [
          "You can be proud of who you are becoming and quietly heartbroken about who you no longer get to be.",
          "Both are real. Holding both is the actual work.",
          "If you only allow yourself the celebration, the grief comes out sideways. Through irritability, through self-sabotage, through a strange flatness on days that should feel good.",
        ],
      },
      {
        heading: "What the grief is teaching",
        paragraphs: [
          "Pay attention to what you are missing.",
          "You may be missing the chaos because chaos felt like aliveness. You may be missing the smallness because smallness felt safe. You may be missing the person who needed you, because being needed gave you a role.",
          "Each of those is real information about what your next chapter has to consciously include in a healthier form.",
        ],
      },
      {
        heading: "Bury the version who got you here",
        paragraphs: [
          "Not violently. With gratitude.",
          "Write the letter you will never send. Light something. Take a walk. Say her name. Tell him thank you.",
          "Then walk through the new door without dragging the entire old house behind you.",
        ],
      },
    ],
    closingInvitation:
      "If the goodbye has stretched on without ever being formally said, Start Here. The next move begins as a quieter conversation with yourself than you think.",
    faqs: [
      {
        q: "Why does growth feel like loss?",
        a: "Because becoming someone new often requires the death of who you were. The grief is not a sign you are failing. It is the body honoring what you outgrew.",
      },
      {
        q: "How do I mourn an old version of myself?",
        a: "Name what she did well. Thank her for it specifically. Let her have her own ending. The version that comes next can only show up if the previous one is allowed to leave with dignity.",
      },
    ],
    reflectionQuestion:
      "What version of you carried you here and has not yet been thanked or formally retired?",
    ctaLabel: "Start Here",
    ctaHref: "/start-here",
    relatedSlugs: ["avoiding-grief-of-growth", "stop-abandoning-who-you-are", "waiting-room-is-not-wasted-time"],
  },

  // ────────────────────────────────────────────────────────────────
  // 6. THE ROOM CHANGES WHEN YOU STOP PERFORMING
  // ────────────────────────────────────────────────────────────────
  {
    id: 26,
    slug: "room-changes-when-you-stop-performing",
    title: "The Room Changes When You Stop Performing.",
    excerpt:
      "Most of the people who love your performance have never met you. Peace begins the day performance stops being the price of belonging.",
    metaTitle: "The Room Changes When You Stop Performing",
    metaDescription:
      "You can spend years being loved for a version of yourself you learned to present. The real life starts when the stage light turns off.",
    ogTitle: "The Room Changes When You Stop Performing",
    ogDescription:
      "If belonging requires the costume, it was never belonging. It was a casting call.",
    primarySeoPhrase: "tired of performing in relationships",
    aeoQuestion: "Why do I feel exhausted around the people who love me?",
    themeTags: ["Relationships"],
    primaryPath: "snapshot",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "stage-light-off",
      prompt:
        "A theater stage from the audience perspective, single spotlight just fading off, leaving warm gold residual glow. The stage is empty but you can sense a person about to step out. Cinematic editorial still life.",
      alt: "A theater spotlight fading off above an empty stage, symbolizing peace after the performance ends.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Many of the people who love you have never actually met you. They have met the performance. Peace starts the day you stop paying for belonging with a version of yourself that has to keep paying.",
    opening: [
      "Notice who in your life gets tired when you have a bad day.",
      "Not who shows up. Who gets tired. Who changes the subject. Who needs you to be okay again so the dynamic can return to its usual choreography.",
      "Some relationships are real.",
      "Some are agreements about the role you have agreed to play in someone else's emotional comfort.",
      "There is a difference. You usually feel it in your shoulders.",
    ],
    bodySections: [
      {
        heading: "Performance is a survival strategy",
        paragraphs: [
          "You did not start performing because you were vain.",
          "You started because somewhere along the way, the real you was not received well. So you sharpened the version that was. The funny one. The capable one. The agreeable one. The one who never needed anything.",
          "It worked. People liked her. People liked him. People kept coming back.",
          "And here you are, twenty years later, exhausted by being chosen for a version of you that is not actually the one underneath.",
        ],
      },
      {
        heading: "The light has a cost",
        paragraphs: [
          "The performance kept the room warm. It also kept you alone in plain sight.",
          "Nobody can love what they have never been allowed to see. Not because they would not. Because they cannot.",
          "You have been collecting love for a costume. The person inside the costume has been getting almost none.",
        ],
      },
      {
        heading: "Turning the light off",
        paragraphs: [
          "When the performance ends, the room reorganizes itself.",
          "Some people lean closer. They were always there waiting for the real you. They will be quieter than the applause but more present than it.",
          "Others will get uncomfortable. They were attached to the role, not to you. Their discomfort is not your job to manage.",
          "Both of these reactions are useful information.",
        ],
      },
      {
        heading: "Belonging without the costume",
        paragraphs: [
          "It starts smaller than you expect.",
          "One conversation where you tell the truth before you edit it. One no without an apology. One quiet day where you do not have to be impressive.",
          "Each one is small. Each one is also evidence to the body that you are allowed to take up space without dressing up for it.",
        ],
      },
    ],
    closingInvitation:
      "If you cannot tell anymore where the performance ends and you begin, the Clarity Snapshot was built for that question. Take it before you talk to anyone else about it.",
    faqs: [
      {
        q: "Why do I feel exhausted around the people who love me?",
        a: "Because the version of you they love may require constant maintenance. Performance is one of the most expensive things the nervous system can hold.",
      },
      {
        q: "How do I stop performing without losing my relationships?",
        a: "You stop in small experiments. One real answer. One unfiltered no. Notice who leans in and who leans out. Both are answers.",
      },
    ],
    reflectionQuestion:
      "Who in your life would feel betrayed if you stopped performing the role they have been loving you for?",
    ctaLabel: "Take the Clarity Snapshot",
    ctaHref: "/frequency-snapshot",
    relatedSlugs: ["finding-yourself-without-performance", "stop-abandoning-who-you-are", "stop-confusing-familiar-with-safe"],
  },

  // ────────────────────────────────────────────────────────────────
  // 7. WHAT YOU CALL DISCIPLINE MIGHT BE SELF-ABANDONMENT
  // ────────────────────────────────────────────────────────────────
  {
    id: 27,
    slug: "discipline-or-self-abandonment",
    title: "What You Keep Calling Discipline Might Be Self-Abandonment.",
    excerpt:
      "There is a difference between a coach who trains you and a critic who breaks you. If your discipline sounds like punishment, your discipline is the problem.",
    metaTitle: "When Discipline Becomes Self-Abandonment",
    metaDescription:
      "Devotion builds trust with yourself. Punishment teaches your body that you are the enemy. The two often wear the same outfit.",
    ogTitle: "When Discipline Becomes Self-Abandonment",
    ogDescription:
      "A coach teaches the athlete to keep going. A critic teaches the athlete to fear themselves. Choose your inner voice carefully.",
    primarySeoPhrase: "discipline vs self abandonment",
    aeoQuestion: "Is my discipline actually self punishment?",
    themeTags: ["Discipline"],
    primaryPath: "orientation_reset",
    readingTime: "6 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "coach-vs-critic",
      prompt:
        "A boxing gym at dawn, gold light spilling through high windows. One pair of gloves resting neatly on a bench, no figure visible. Quiet, disciplined, not violent. Cinematic editorial still life.",
      alt: "Gloves resting on a bench in a quiet gym at dawn, symbolizing trained discipline rather than self-punishment.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "True discipline builds trust with yourself. Punishment teaches your body that you are the enemy. If your routines run on shame, you are not building. You are surviving yourself.",
    opening: [
      "Two voices can say the exact same sentence.",
      "Get up. Train. Work. Finish what you started.",
      "One voice belongs to a coach who believes you can do it and will not let you cheat yourself out of becoming the person who can.",
      "The other voice belongs to a critic who is terrified of you being lovable while still imperfect.",
      "Same words. Different bloodstream.",
    ],
    bodySections: [
      {
        heading: "Discipline is a relationship",
        paragraphs: [
          "Discipline is not a calendar. It is a way you talk to yourself.",
          "A devoted discipline says I am going to do this thing because I have decided I am the kind of person who does it. There is no debate when the morning is hard. There is also no contempt when the morning is harder than usual.",
          "An abandoning discipline says I have to earn the right to rest, eat, breathe, slow down, or like myself. Failure becomes proof of unworthiness instead of a data point.",
          "The first one builds you. The second one rents you.",
        ],
      },
      {
        heading: "Punishment looks like productivity from the outside",
        paragraphs: [
          "This is why it is hard to catch.",
          "The person punishing themselves usually has nice metrics. They show up. They finish. They look like they have it together.",
          "Inside, they are running on a contract they signed with a younger version of themselves that said you only get to exist if you are useful.",
          "Eventually the body stops believing in the deal and starts dropping the things that mattered most.",
        ],
      },
      {
        heading: "The test",
        paragraphs: [
          "Ask one question.",
          "If I miss this today, do I respond like someone who trusts I will get back to it tomorrow, or like someone who needs to be punished?",
          "If your answer is closer to the second one, your discipline is not training you. It is running you.",
        ],
      },
      {
        heading: "Rebuilding the contract",
        paragraphs: [
          "The shift is not dropping discipline. It is changing who is holding it.",
          "Fire the critic. Hire the coach.",
          "Keep the routines. Lose the shame engine they were running on. Your output may briefly drop. Your sustainability and self-trust will quietly multiply.",
        ],
      },
    ],
    closingInvitation:
      "If you cannot tell which voice is running your discipline anymore, the Clarity Reset Session is a single conversation built to name it. One call. One audit. One coaching voice on your side by the end.",
    faqs: [
      {
        q: "Is my discipline actually self punishment?",
        a: "Notice how you treat yourself on the days you fall short. Discipline returns you to the routine. Punishment uses the slip as evidence against you.",
      },
      {
        q: "Can I be disciplined and gentle at the same time?",
        a: "Yes. The best long-term discipline is gentle in tone and firm in direction. Cruelty rarely creates anything sustainable.",
      },
    ],
    reflectionQuestion:
      "If your inner voice spoke to a kid you loved the way it speaks to you, would the kid feel coached or condemned?",
    ctaLabel: "Book the Clarity Reset Session",
    ctaHref: "#book-orientation-reset",
    relatedSlugs: ["lack-confidence-breaking-self-trust", "you-are-not-lazy-you-are-overloaded", "addicted-to-potential"],
  },

  // ────────────────────────────────────────────────────────────────
  // 8. YOU DO NOT NEED MORE NOISE. YOU NEED A CLEANER QUESTION.
  // ────────────────────────────────────────────────────────────────
  {
    id: 28,
    slug: "you-do-not-need-noise-cleaner-question",
    title: "You Do Not Need More Noise. You Need a Cleaner Question.",
    excerpt:
      "Most people are searching the wrong room with the wrong flashlight. Change the question, and the room changes with it.",
    metaTitle: "You Do Not Need More Noise. You Need a Cleaner Question.",
    metaDescription:
      "Better questions reveal better paths. The quality of your next season depends on the quality of the question you are willing to actually ask.",
    ogTitle: "You Do Not Need More Noise. You Need a Cleaner Question.",
    ogDescription:
      "Searching a messy room with the wrong flashlight is not a flashlight problem. It is a question problem.",
    primarySeoPhrase: "better questions for clarity",
    aeoQuestion: "How do I ask better questions about my life?",
    themeTags: ["Reflection"],
    primaryPath: "snapshot",
    readingTime: "4 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "flashlight",
      prompt:
        "A single golden flashlight beam cutting through a dim room, illuminating dust in the air. Books and notes on the floor barely visible. Cinematic editorial still life, intimate and quiet.",
      alt: "A flashlight beam cutting through a dim room, symbolizing the moment a better question reveals a hidden path.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Better questions reveal better paths. Most of what feels like confusion is a vague question. Sharpen the question and the next move usually becomes obvious.",
    opening: [
      "You can search a messy room with the wrong flashlight for hours.",
      "It is not the room's fault. It is not the flashlight's fault either.",
      "You were asking where is it. You should have been asking what would it look like if it were here.",
      "Most of what we call confusion is really a vague question. We say I do not know what to do. The honest version is closer to I have not yet decided what I am actually asking.",
    ],
    bodySections: [
      {
        heading: "Big questions block. Specific questions move.",
        paragraphs: [
          "What do I want to do with my life is a question that has put more people in bed than any other.",
          "What would I regret most not having attempted by the time I am sixty is a question that gets answered in under a minute.",
          "Same general territory. Completely different result.",
          "A useful question is small enough to have an answer and honest enough to make you uncomfortable.",
        ],
      },
      {
        heading: "Sharpen by subtraction",
        paragraphs: [
          "Take the question you have been carrying.",
          "Cross out the parts that are about other people. The judgments. The maybe-they-will-think. The will-this-look-bad.",
          "What remains is usually the actual question. It is almost always shorter than you thought.",
        ],
      },
      {
        heading: "Some questions you have been avoiding",
        paragraphs: [
          "What do I keep waiting for permission to say.",
          "Who in my life would not survive if I told the full truth.",
          "What would I do tomorrow if no one was watching the result.",
          "What is the thing I keep almost saying.",
          "Pick one. Sit with it long enough to want to look away. The answer is usually right where the discomfort is loudest.",
        ],
      },
    ],
    closingInvitation:
      "If you cannot pick the question yet, the Clarity Snapshot is ten of them. Ten honest questions, ninety seconds, one written reflection back.",
    faqs: [
      {
        q: "How do I ask better questions about my life?",
        a: "Make them small. Make them honest. Subtract anything about other people's reactions until what remains is the actual question.",
      },
      {
        q: "Why does asking a sharper question feel scary?",
        a: "Because a sharper question is harder to dodge. The vagueness was protecting you from the answer.",
      },
    ],
    reflectionQuestion:
      "What is the question underneath the question you have been asking out loud?",
    ctaLabel: "Take the Clarity Snapshot",
    ctaHref: "/frequency-snapshot",
    relatedSlugs: ["stop-asking-for-a-sign", "want-clarity-rewarding-chaos", "moment-before-clarity-feels-like-confusion"],
  },

  // ────────────────────────────────────────────────────────────────
  // 9. SOME DOORS ONLY OPEN AFTER YOU STOP KNOCKING LIKE THE OLD YOU
  // ────────────────────────────────────────────────────────────────
  {
    id: 29,
    slug: "doors-open-different-identity",
    title: "Some Doors Only Open After You Stop Knocking Like the Old You.",
    excerpt:
      "The same opportunity can respond differently when you approach it from a different identity. The key may not be the problem. The angle is.",
    metaTitle: "Why the Same Opportunity Responds Differently to a Different You",
    metaDescription:
      "Access is sometimes not blocked. The approach is unmatured. The same door can stay locked for years until the version of you reaching for it grows up.",
    ogTitle: "Why the Same Opportunity Responds Differently to a Different You",
    ogDescription:
      "A key that fits the lock can still fail at the wrong angle. The hand matters as much as the key.",
    primarySeoPhrase: "same opportunity different identity",
    aeoQuestion: "Why do the same opportunities keep saying no?",
    themeTags: ["Purpose", "Momentum"],
    primaryPath: "blueprint",
    readingTime: "6 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "key-and-door",
      prompt:
        "A weathered brass key resting in the palm of a steady hand near an old dark wooden door with a brass keyhole. Warm gold light catching the key. Cinematic editorial still life, premium magazine.",
      alt: "A brass key held near a wooden door, symbolizing access that depends on the angle of approach.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Sometimes access is not blocked. Your approach needs to mature. The same opportunity can respond differently when the person reaching for it has grown into a different identity.",
    opening: [
      "There is an opportunity in your life that has said no more than once.",
      "Same room. Same door. Same key. Different result every time. You start to think maybe it just is not for you.",
      "Pause for a second.",
      "What if the key was not the problem? What if the door has been waiting for a different hand?",
    ],
    bodySections: [
      {
        heading: "The lock can read more than you think",
        paragraphs: [
          "Opportunities do not just respond to credentials. They respond to posture.",
          "How you walk in. What you assume the room owes you. What you assume you owe the room. Whether you are pitching from need or arriving from steadiness.",
          "Two people with the same resume can walk into the same room and get completely different answers, because the room is not reading the resume. It is reading the person.",
        ],
      },
      {
        heading: "The old you was probably trying to prove something",
        paragraphs: [
          "Look at the last time you reached for this door.",
          "Were you trying to be enough. Were you trying to be seen. Were you trying to outrun a story about your worth.",
          "Doors trained to recognize hunger sometimes close in the face of it. Not because hunger is wrong. Because hunger that is unmet inside you tends to ask the room to fix something the room cannot fix.",
        ],
      },
      {
        heading: "What changes when the identity changes",
        paragraphs: [
          "When the inside catches up, the asking gets quieter.",
          "You stop selling yourself in a tone that says please. You start describing yourself in a tone that says this is who I am, and this is what I bring, and if it is a fit, it is a fit.",
          "Some doors only respond to that voice. They were never going to open for the old one.",
        ],
      },
      {
        heading: "Before you knock again",
        paragraphs: [
          "Ask whether you have changed since the last time.",
          "Not your strategy. You. Your sense of your own worth. Your tolerance for waiting. Your willingness to walk away if the door does not respond.",
          "If you have not, do that work first. The door will still be there. It might even open differently.",
        ],
      },
    ],
    closingInvitation:
      "The Personal Action Plan is where this becomes structural, not just a feeling. It maps what you are becoming and what each version of you is actually equipped to ask for.",
    faqs: [
      {
        q: "Why do the same opportunities keep saying no?",
        a: "The opportunity may be reading the version of you reaching for it. Identity work changes what the same door responds to.",
      },
      {
        q: "How do I know if I have actually changed enough to try again?",
        a: "The asking sounds different from the inside. Less proving, more steady. If you are still rehearsing your worth before the conversation, the work may not be finished yet.",
      },
    ],
    reflectionQuestion:
      "What door have you been knocking on, and what would change if a steadier version of you walked up to it tomorrow?",
    ctaLabel: "Explore the Personal Action Plan",
    ctaHref: "/?checkout=blueprint#blueprint",
    relatedSlugs: ["force-door-open-not-your-door", "addicted-to-potential", "ego-sounds-like-protection"],
  },

  // ────────────────────────────────────────────────────────────────
  // 10. THE MOMENT BEFORE CLARITY OFTEN FEELS LIKE CONFUSION
  // ────────────────────────────────────────────────────────────────
  {
    id: 30,
    slug: "moment-before-clarity-feels-like-confusion",
    title: "The Moment Before Clarity Often Feels Like Confusion.",
    excerpt:
      "Driving through fog is not failure. It is the part where the old map stops working and the next one has not arrived yet. Stay on the road.",
    metaTitle: "Why Clarity Often Arrives Through Confusion",
    metaDescription:
      "Confusion is not always a sign that something is wrong. Sometimes it means the old map no longer fits the road you are actually on.",
    ogTitle: "Why Clarity Often Arrives Through Confusion",
    ogDescription:
      "Fog before a clear road is not failure. It is the transition the old map could not survive.",
    primarySeoPhrase: "confusion before clarity",
    aeoQuestion: "Why do I feel confused even though I should be clear?",
    themeTags: ["Transition"],
    primaryPath: "start-here",
    readingTime: "5 min read",
    publishedAt: PUBLISHED_V2,
    updatedAt: UPDATED_V2,
    featuredImage: {
      motif: "fog-road",
      prompt:
        "A two-lane road disappearing into morning fog, a single soft warm gold light glowing through the mist at the horizon. Cinematic editorial still life, premium magazine feel, no car or person.",
      alt: "An empty road vanishing into morning fog with a faint gold glow ahead, symbolizing clarity arriving through confusion.",
    },
    directAnswerLabel: "The short truth",
    directAnswer:
      "Confusion is not always failure. Sometimes it means the old map stopped working and the new one has not yet been drawn. Do not mistake transition for being lost.",
    opening: [
      "You are driving and the fog rolls in.",
      "You do not pull over and announce that the road has betrayed you. You slow down. You turn the high beams off because they make it worse. You keep your hands steady and you watch the line on the right.",
      "You do not need the whole road. You need ten feet of road, repeatedly.",
      "Most of life works the same way. People keep waiting for the whole road to appear before they will move. They call the fog confusion. They call the confusion a sign that something is wrong.",
      "It is not wrong. It is just fog.",
    ],
    bodySections: [
      {
        heading: "Old maps stop working before new ones arrive",
        paragraphs: [
          "Confusion shows up most often at thresholds.",
          "The end of an identity. The end of a season. The end of an assumption you held for so long it never had to defend itself.",
          "The body knows something has shifted before the mind builds the new framework. That gap is what we call confusion. It is mostly a translation delay.",
        ],
      },
      {
        heading: "Stop trying to solve fog",
        paragraphs: [
          "You cannot brainstorm your way out of a foggy chapter. You can only walk through it with attention.",
          "What you can do is keep ten feet of road in front of you.",
          "What is the one honest thing I know about this week. What is the next clearly aligned action. What can I cancel. What can I close. What can I leave alone until something becomes more visible.",
          "Small steady moves clear more fog than dramatic decisions made from a place of internal weather.",
        ],
      },
      {
        heading: "Trust the road, not the visibility",
        paragraphs: [
          "There is a different kind of trust required at this stage.",
          "You will not be able to see proof of your direction every day. You will have to take the next correct step before the path looks like a path.",
          "This is uncomfortable. It is also the only way new chapters ever begin.",
        ],
      },
      {
        heading: "When the fog lifts",
        paragraphs: [
          "It rarely lifts dramatically.",
          "Usually you just notice one morning that you have been driving steady for a while. The road is visible again. You can see further than ten feet. The thing you were terrified of weeks ago is now a few miles behind you.",
          "Confusion was never the problem. The willingness to keep moving through it is what changed everything.",
        ],
      },
    ],
    closingInvitation:
      "If the fog has been longer than usual, Start Here. The next ten feet of road is enough.",
    faqs: [
      {
        q: "Why do I feel confused even though I should be clear?",
        a: "Because clarity is usually delayed by a translation gap between the body noticing change and the mind building a new framework. Confusion is often the gap, not the verdict.",
      },
      {
        q: "How do I keep moving when nothing is clear yet?",
        a: "Stop trying to see the whole road. Take the next ten feet. Repeat. Most clarity arrives through small steady action, not a single dramatic insight.",
      },
    ],
    reflectionQuestion:
      "What is the next ten feet of road you could honestly take this week, even if the rest of the path is still in fog?",
    ctaLabel: "Start Here",
    ctaHref: "/start-here",
    relatedSlugs: ["waiting-room-is-not-wasted-time", "next-level-feels-like-grief", "stop-asking-for-a-sign"],
  },
];

export default JOURNAL_POSTS_V2;
